import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import Icon from '@components/Icon';
import { Button, Tooltip, Popover, Segmented, ConfigProvider, Popconfirm, Slider, InputNumber, Tabs, Radio } from 'antd';
import stores from '@stores';
import { toDownloadFile, nanoid, modKey } from '@utils/utils';
import useKeyboardShortcuts from '@hooks/useKeyboardShortcuts';

export default observer(() => {
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [format, setFormat] = useState('png');
    const [ratio, setRatio] = useState(1);
    const [canCopy, setCanCopy] = useState(true);
    const [customWidth, setCustomWidth] = useState(null);
    const [customHeight, setCustomHeight] = useState(null);
    const [resolutionMode, setResolutionMode] = useState('ratio'); // 'ratio' or 'custom'
    
    // 当选择不同的分辨率模式或比例变化时，更新自定义宽高
    useEffect(() => {
        if (stores.option.frameConf.width && resolutionMode === 'ratio') {
            setCustomWidth(Math.round(stores.option.frameConf.width * ratio));
            setCustomHeight(Math.round(stores.option.frameConf.height * ratio));
        }
    }, [ratio, resolutionMode, stores.option.frameConf.width, stores.option.frameConf.height]);
    
    // 检查浏览器是否支持Clipboard API
    useEffect(() => {
        const isClipboardSupported = !!navigator.clipboard && 
            typeof navigator.clipboard.write === 'function';
        setCanCopy(isClipboardSupported);
    }, []);
    
    const handleOpenChange = (newOpen) => {
        setOpen(newOpen);
    };
    
    // 维持宽高比例
    const handleWidthChange = (value) => {
        setCustomWidth(value);
        if (stores.option.frameConf.width && stores.option.frameConf.height) {
            const aspectRatio = stores.option.frameConf.width / stores.option.frameConf.height;
            setCustomHeight(Math.round(value / aspectRatio));
        }
    };
    
    const handleHeightChange = (value) => {
        setCustomHeight(value);
        if (stores.option.frameConf.width && stores.option.frameConf.height) {
            const aspectRatio = stores.option.frameConf.width / stores.option.frameConf.height;
            setCustomWidth(Math.round(value * aspectRatio));
        }
    };
    
    const toDownload = async () => {
        if (!stores.editor.isEditing) return;
        if (loading) return;
        
        // 准备导出选项
        const option = {};
        
        // 根据分辨率模式设置导出尺寸
        if (resolutionMode === 'ratio') {
            option.pixelRatio = ratio;
        } else if (resolutionMode === 'custom' && customWidth && customHeight) {
            option.width = customWidth;
            option.height = customHeight;
        }
        
        // 为jpg和webp格式添加填充颜色和质量选项
        if (['jpg', 'webp'].includes(format)) {
            option.quality = 0.9;
            option.fill = '#ffffff';
        }
        
        const key = nanoid();
        setLoading(true);
        
        // 显示加载提示
        stores.editor.message.open({
            key,
            type: 'loading',
            content: '正在下载...',
        });
        
        try {
            // 添加回调函数处理Canvas，以应用滤镜效果
            if (stores.option.enableFilter && stores.option.filterString) {
                option.callback = (canvas) => {
                    // 创建临时Canvas以保存原始图像
                    const tempCanvas = document.createElement('canvas');
                    tempCanvas.width = canvas.width;
                    tempCanvas.height = canvas.height;
                    const tempCtx = tempCanvas.getContext('2d');
                    
                    // 复制原始图像到临时Canvas
                    tempCtx.drawImage(canvas, 0, 0);
                    
                    // 在原始Canvas上应用滤镜
                    const ctx = canvas.getContext('2d');
                    ctx.filter = stores.option.filterString;
                    ctx.clearRect(0, 0, canvas.width, canvas.height);
                    ctx.drawImage(tempCanvas, 0, 0);
                    
                    // 重置滤镜，避免影响其他操作
                    ctx.filter = 'none';
                }
            }
            
            // 导出图像
            const result = await stores.editor.app.tree.export(format, option);
            
            // 下载文件
            let name = `图美啦`;
            if (resolutionMode === 'ratio' && ratio > 1) {
                name += `@${ratio}`;
            } else if (resolutionMode === 'custom') {
                name += `_${customWidth}x${customHeight}`;
            }
            toDownloadFile(result.data, `${name}.${format}`);
            
            // 显示成功消息
            stores.editor.message.open({
                key,
                type: 'success',
                content: '下载成功！',
            });
        } catch (error) {
            console.error('导出图像失败:', error);
            
            // 显示错误消息
            stores.editor.message.open({
                key,
                type: 'error',
                content: '下载失败！',
            });
        } finally {
            // 重置加载状态
            setLoading(false);
        }
    };
    
    const toCopy = async () => {
        if (!stores.editor.isEditing) return;
        if (loading) return;
        if (!canCopy) {
            stores.editor.message.info('当前浏览器不支持直接复制图片，请使用下载功能');
            return;
        }
        
        const key = nanoid();
        setLoading(true);
        
        stores.editor.message.open({
            key,
            type: 'loading',
            content: '正在复制...',
        });
        
        try {
            // 准备导出选项
            const option = { blob: true };
            
            // 根据分辨率模式设置导出尺寸
            if (resolutionMode === 'ratio') {
                option.pixelRatio = ratio;
            } else if (resolutionMode === 'custom' && customWidth && customHeight) {
                option.width = customWidth;
                option.height = customHeight;
            }
            
            // 添加回调函数处理Canvas，以应用滤镜效果
            if (stores.option.enableFilter && stores.option.filterString) {
                option.callback = (canvas) => {
                    // 创建临时Canvas以保存原始图像
                    const tempCanvas = document.createElement('canvas');
                    tempCanvas.width = canvas.width;
                    tempCanvas.height = canvas.height;
                    const tempCtx = tempCanvas.getContext('2d');
                    
                    // 复制原始图像到临时Canvas
                    tempCtx.drawImage(canvas, 0, 0);
                    
                    // 在原始Canvas上应用滤镜
                    const ctx = canvas.getContext('2d');
                    ctx.filter = stores.option.filterString;
                    ctx.clearRect(0, 0, canvas.width, canvas.height);
                    ctx.drawImage(tempCanvas, 0, 0);
                    
                    // 重置滤镜，避免影响其他操作
                    ctx.filter = 'none';
                }
            }
            
            const result = await stores.editor.app.tree.export('png', option);
            const { data } = result;
            
            await navigator.clipboard.write([
                new ClipboardItem({
                    [data.type]: data,
                }),
            ]);
            
            stores.editor.message.open({
                key,
                type: 'success',
                content: '复制成功！',
            });
        } catch (error) {
            console.error('复制图像失败:', error);
            
            stores.editor.message.open({
                key,
                type: 'error',
                content: '复制失败！',
            });
        } finally {
            setLoading(false);
        }
    }
    
    const confirm = () => {
        stores.editor.destroy();
        stores.editor.clearImg();
        stores.editor.clearFun && stores.editor.clearFun();
    }
    
    useKeyboardShortcuts(() => toDownload(), () => toCopy(), [toDownload, toCopy]);
    
    // 展示当前的下载尺寸信息
    const getDownloadSizeInfo = () => {
        if (!stores.option.frameConf.width) return null;
        
        if (resolutionMode === 'ratio') {
            return (
                <div className="text-xs p-3 mt-4 flex justify-between bg-black/5 rounded-md">
                    <span className="text-gray-400">下载尺寸</span>
                    <span className="text-gray-700">{Math.round(stores.option.frameConf.width * ratio)} x {Math.round(stores.option.frameConf.height * ratio)}</span>
                </div>
            );
        } else {
            return (
                <div className="text-xs p-3 mt-4 flex justify-between bg-black/5 rounded-md">
                    <span className="text-gray-400">下载尺寸</span>
                    <span className="text-gray-700">{customWidth} x {customHeight}</span>
                </div>
            );
        }
    };
    
    const formatTabItems = [
        {
            key: 'basic',
            label: '基本格式',
            children: (
                <Radio.Group 
                    className="w-full flex justify-between" 
                    value={format} 
                    onChange={(e) => setFormat(e.target.value)}
                >
                    <Radio.Button value="png" className="flex-1 text-center">PNG</Radio.Button>
                    <Radio.Button value="jpg" className="flex-1 text-center">JPG</Radio.Button>
                    <Radio.Button value="webp" className="flex-1 text-center">WEBP</Radio.Button>
                    <Radio.Button value="bmp" className="flex-1 text-center">BMP</Radio.Button>
                </Radio.Group>
            )
        }
    ];
    
    const resolutionTabItems = [
        {
            key: 'ratio',
            label: '默认比例',
            children: (
                <>
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-500">缩放比例</span>
                        <InputNumber
                            min={0.5}
                            max={5}
                            step={0.5}
                            value={ratio}
                            onChange={setRatio}
                            size="small"
                            className="w-20"
                            addonAfter="x"
                        />
                    </div>
                    <Slider
                        min={0.5}
                        max={5}
                        step={0.5}
                        value={ratio}
                        onChange={setRatio}
                        marks={{
                            0.5: '0.5x',
                            1: '1x',
                            2: '2x',
                            3: '3x',
                            4: '4x',
                            5: '5x'
                        }}
                        tooltip={{
                            formatter: (value) => `${value}x`
                        }}
                    />
                </>
            )
        },
        {
            key: 'custom',
            label: '自定义尺寸',
            children: (
                <>
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-500">宽度</span>
                        <InputNumber
                            min={50}
                            max={10000}
                            value={customWidth}
                            onChange={handleWidthChange}
                            size="small"
                            className="w-24"
                            addonAfter="px"
                        />
                    </div>
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-500">高度</span>
                        <InputNumber
                            min={50}
                            max={10000}
                            value={customHeight}
                            onChange={handleHeightChange}
                            size="small"
                            className="w-24"
                            addonAfter="px"
                        />
        </div>
                </>
            )
        }
    ];
    
    const content = (
        <div className="p-2">
            <div className="mb-4">
                <div className="text-xs text-gray-400 mb-2 font-medium">图片格式</div>
                <Tabs items={formatTabItems} />
            </div>
            
            <div className="mb-2">
                <div className="text-xs text-gray-400 mb-2 font-medium">分辨率设置</div>
                <Tabs 
                    items={resolutionTabItems} 
                    activeKey={resolutionMode}
                    onChange={setResolutionMode}
                />
            </div>
            
            {getDownloadSizeInfo()}
            
            <div className="mt-6 flex gap-2">
            <ConfigProvider
                theme={{
                    components: {
                        Button: {
                                colorPrimary: stores.editor.isDark ? '#7c3aed' : '#7c3aed',
                                borderRadius: 8,
                                algorithm: true,
                            },
                    },
                }}
            >
                        <Button
                            type='primary'
                        size='middle'
                        block
                            loading={loading}
                        icon={<Icon.ImageDown size={16} className="mr-1" />}
                            onClick={toDownload}
                        className="py-5 flex items-center justify-center h-auto"
                    >
                        <span className="font-medium">
                            下载{format.toUpperCase()}图片
                        </span>
                        </Button>
                    
                        <Button
                        size='middle'
                        icon={<Icon.Copy size={16} className="mr-1" />}
                            loading={loading}
                            onClick={toCopy}
                            disabled={!canCopy}
                        className="py-5 flex items-center justify-center h-auto"
                    >
                        <span className="font-medium">
                            复制到剪贴板
                        </span>
                    </Button>
                </ConfigProvider>
            </div>
                </div>
    );
            
    return (
        <div className='shrink-0 py-4 px-6 flex gap-2 justify-center items-center'>
            <div className="flex items-center gap-1">
                <Popover
                    content={content}
                    trigger='click'
                    arrow={false}
                    placement="topRight"
                    open={open}
                    overlayStyle={{
                        width: '350px',
                    }}
                    onOpenChange={handleOpenChange}
                >
                    <Button 
                        size='large' 
                        type="primary"
                        className="bg-purple-600 hover:bg-purple-700"
                        icon={<Icon.ImageDown size={18} className="mr-1" />} 
                    >
                        导出设置
                    </Button>
                </Popover>
                {stores.editor.img?.src && 
                    <Popconfirm
                        title="删除截图"
                        description="确定要删除这张截图吗？"
                        placement="topRight"
                        onConfirm={confirm}
                        okText="确定"
                        cancelText="取消"
                    >
                        <Button 
                            size='large' 
                            type="primary"
                            danger
                            icon={<Icon.Trash2 size={18} className="mr-1" />}
                        >
                            删除图片
                        </Button>
                    </Popconfirm>
                }
            </div>
        </div>
    );
});
